import { useState } from "react";
import "./mobile-news.css";
import { useNavigate } from "react-router-dom";
import heroImage from "@/assets/case-studies-hero.jpg";
import { NsButton } from "@/components/new-site/NewSiteChrome";
import { Button } from "@/components/ui/button";
import { featuredStory, storiesByDate, topics, type Story, type Topic } from "./stories";

function StoryBadge({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex min-h-6 items-center bg-ns-lime px-5 py-1 text-[12px] font-medium leading-[19.1px]">{children}</span>;
}

function StoryCard({ story, onRead }: { story: Story; onRead: (story: Story) => void }) {
  return (
    <article className="flex min-w-0 flex-col gap-5 rounded-lg border border-ns-plum/50 p-4">
      <img src={story.image} alt={story.imageAlt} loading="lazy" className="h-[180px] w-full rounded-md object-cover" />
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{story.date}</p>
          <StoryBadge>{story.category}</StoryBadge>
        </div>
        <h3 className="text-[15px] font-medium leading-[1.5]">{story.title}</h3>
        <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{story.summary}</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ns-plum/50 pt-5">
        <NsButton onClick={() => onRead(story)}>Read Article</NsButton>
        <span className="text-[15px] leading-[1.5] text-ns-plum/50">5 Min Read</span>
      </div>
    </article>
  );
}

/** Private Figma editorial preview; never substitutes content on the live case studies page. */
export default function NewSiteNewsContent({ onContact }: { onContact?: () => void }) {
  const [topic, setTopic] = useState<Topic>("All Stories");
  const navigate = useNavigate();
  const setSelectedStory = (story: Story) => navigate(`/news/${story.id}`);
  const [allLoaded, setAllLoaded] = useState(false);
  const visibleStories = topic === "All Stories" ? storiesByDate : [featuredStory, ...storiesByDate].filter((story) => story.topic === topic);

  return (
    <div className="ns-news">
      <section aria-labelledby="news-heading" className="relative flex min-h-[570px] items-end overflow-hidden bg-ns-plum px-6 py-14 md:min-h-[max(520px,min(680px,calc(100svh-100px)))] md:px-[72px] md:py-[72px] xl:min-h-[max(520px,min(782px,calc(100svh-100px)))]">
        <img src={heroImage} alt="Snapshot Live autonomous camera installed beside a waterway" className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="ns-news-hero-shade absolute inset-0" />
        <div className="relative flex w-full max-w-[706px] flex-col items-start gap-[29px] text-ns-white">
          <p className="text-[12px] font-medium leading-[19.1px]">Latest News &amp; Insights</p>
          <h1 id="news-heading" className="text-[32px] font-medium leading-[1.15] md:text-[40px]">Snapshot Live News</h1>
        </div>
      </section>

      <section aria-labelledby="featured-heading" className="ns-news-featured-bg px-6 pb-16 pt-20 md:px-20">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-12">
          <h2 id="featured-heading" className="text-[28px] font-medium leading-[1.15] md:text-[40px]">Featured Story</h2>
          <article className="grid items-center gap-8 rounded-lg border border-ns-plum/50 bg-ns-white p-5 lg:grid-cols-2 lg:gap-12 lg:p-8">
            <img src={featuredStory.image} alt={featuredStory.imageAlt} className="h-[280px] w-full rounded-lg object-cover lg:h-[380px]" />
            <div className="flex min-w-0 flex-col items-start gap-5">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{featuredStory.date}</p>
                <StoryBadge>{featuredStory.category}</StoryBadge>
              </div>
              <h3 className="max-w-[537px] text-[24px] font-medium leading-[1.3] md:text-[27px]">{featuredStory.title}</h3>
              <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{featuredStory.summary}</p>
              <div className="flex flex-wrap items-center gap-4">
                <NsButton onClick={() => setSelectedStory(featuredStory)}>Read Article</NsButton>
                <span className="text-[15px] font-medium leading-[1.5]">5 Min Read</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section aria-label="Latest stories" className="bg-ns-white px-6 pb-24 pt-[70px] md:px-20">
        <div className="mx-auto max-w-[1280px]">
          <div role="group" aria-label="Filter by topic" className="ns-news-filters mb-8 flex flex-wrap items-center gap-3">
            <span className="text-[15px] font-medium leading-[1.5]">Filter by Topic:</span>
            {topics.map((item) => (
              <Button key={item} variant="ghost" aria-pressed={topic === item} onClick={() => { setTopic(item); setAllLoaded(false); }} className={`h-6 rounded-full border border-ns-plum px-5 py-0 text-[14px] font-medium text-ns-plum hover:bg-ns-lime hover:text-ns-plum focus-visible:ring-ns-plum ${topic === item ? "bg-ns-lime" : "bg-transparent"}`}>{item}</Button>
            ))}
          </div>
          <div aria-live="polite" className="grid items-stretch gap-8 md:grid-cols-2 xl:grid-cols-3">
            {visibleStories.map((story) => <StoryCard key={story.id} story={story} onRead={setSelectedStory} />)}
          </div>
          {visibleStories.length === 0 && <p role="status" className="pt-6 text-[15px] leading-[1.5] text-ns-plum/50">No stories in this topic yet. New articles are on the way.</p>}
          {visibleStories.length > 0 && (
            <div className="flex justify-center pt-14">
              {allLoaded ? <p role="status" className="text-[14px] text-ns-plum/50">All stories loaded</p> : <NsButton onClick={() => setAllLoaded(true)}>Load More Stories</NsButton>}
            </div>
          )}
        </div>
      </section>

      <section aria-labelledby="news-cta-heading" className="ns-news-cta-bg flex flex-col items-center gap-8 px-6 py-20 text-center md:px-[72px] md:py-[120px]">
        <div className="flex w-full max-w-[850px] flex-col items-center gap-4">
          <h2 id="news-cta-heading" className="text-[28px] font-medium leading-[1.15] md:text-[40px]">Secure Your Operational Visibility Today</h2>
          <p className="max-w-[536px] text-[15px] font-medium leading-[1.5]">Speak with our team to discover how autonomous, image-based monitoring can improve visibility, reduce costs and help you make smarter, faster decisions.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <NsButton variant="lime" onClick={onContact}>Contact Us</NsButton>
          <NsButton onClick={onContact}>Request Brochure</NsButton>
        </div>
      </section>

    </div>
  );
}